'use client';

import { useCallback, useEffect, useState } from 'react';

import { xiCafeBookingsList } from '../lib/client';
import { ConnectionStatus } from '../lib/connection-status';
import {
  determineXiCafeConnectionStatus,
  mapXiCafeBookingToTariff,
} from '../lib/determine-connection-status';

import type { XiCafeTariff } from '../lib/types';

export type UseXiCafeStatusArgs = {
  mpi: string | null;
  token: string | null;
};

export type XiCafeStatusState = {
  status: ConnectionStatus;
  tariff: XiCafeTariff | null;
  isLoading: boolean;
};

/**
 * Reads tariff status directly from the XiCafe API (`bookings/list`) in the
 * browser, using the client-side XiCafe JWT as the Bearer token — same as the
 * native app.
 *
 * NOTE: this requires the XiCafe API (`:8085`) to allow the browser's CORS
 * preflight (2xx for `OPTIONS` + `Authorization` in `Access-Control-Allow-Headers`).
 * Until XControl ships that, the call is blocked and the status falls back to
 * `Connected`.
 *
 * Triggers: on mount, and on `document.visibilitychange` → `visible`.
 * On any failure, returns `status: Connected` / `tariff: null` — no user-facing error.
 */
export const useXiCafeStatus = ({
  mpi,
  token,
}: UseXiCafeStatusArgs): XiCafeStatusState => {
  const [state, setState] = useState<XiCafeStatusState>({
    status: ConnectionStatus.Unknown,
    tariff: null,
    isLoading: true,
  });

  const refresh = useCallback(async () => {
    if (!mpi) {
      setState({
        status: ConnectionStatus.Connected,
        tariff: null,
        isLoading: false,
      });
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));
    const response = await xiCafeBookingsList(mpi, token);

    setState({
      status: determineXiCafeConnectionStatus(
        response.success
          ? { success: true, bookings: response.data }
          : { success: false },
      ),
      tariff: response.success ? mapXiCafeBookingToTariff(response.data) : null,
      isLoading: false,
    });
  }, [mpi, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refresh]);

  return state;
};
